// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { replaceHistoryEntry } from "../router/historyIndex";

export type ConnectorCallbackConnector = "google" | "graph" | "docusign";

export interface ConnectorCallbackEventDetail {
  connector: ConnectorCallbackConnector;
  status: "connected" | "error";
  reason?: string;
}

export const CONNECTOR_CALLBACK_EVENT = "riddle-connector-callback";

const CONNECTION_CALLBACK_PATHS = new Map<string, ConnectorCallbackConnector>([
  ["/connections/google/callback", "google"],
  ["/connections/graph/callback", "graph"],
  ["/connections/docusign/callback", "docusign"],
]);

const CONNECTOR_SETTINGS_SECTIONS: Record<ConnectorCallbackConnector, string> = {
  google: "google-workspace-connector",
  graph: "microsoft-365-connector",
  docusign: "docusign-connector",
};

export function connectionCallbackConnector(pathname: string): ConnectorCallbackConnector | null {
  return CONNECTION_CALLBACK_PATHS.get(pathname) ?? null;
}

export function connectorSettingsUrl(
  connector: ConnectorCallbackConnector,
  outcome?: ConnectorCallbackEventDetail,
): string {
  const params = new URLSearchParams({
    tab: "workspace",
    section: CONNECTOR_SETTINGS_SECTIONS[connector],
  });
  if (outcome !== undefined) {
    params.set("connector", connector);
    params.set("status", outcome.status);
    if (outcome.reason !== undefined) {
      params.set("reason", outcome.reason);
    }
  }
  return `/settings?${params.toString()}`;
}

export function parseConnectorCallback(
  search: string,
): ConnectorCallbackEventDetail | null {
  const params = new URLSearchParams(search);
  const connector = params.get("connector");
  if (connector !== "google" && connector !== "graph" && connector !== "docusign") {
    return null;
  }

  const statusParam = params.get("status");
  const status = statusParam === "error" ? "error" : "connected";
  const reason = status === "error" ? params.get("reason") ?? undefined : undefined;
  return reason === undefined
    ? { connector, status }
    : { connector, status, reason };
}

const heldCallbacks = new Map<ConnectorCallbackConnector, ConnectorCallbackEventDetail>();

export function takeHeldConnectorCallback(
  connector: ConnectorCallbackConnector,
): ConnectorCallbackEventDetail | null {
  const detail = heldCallbacks.get(connector) ?? null;
  heldCallbacks.delete(connector);
  return detail;
}

export function clearHeldConnectorCallbacks(): void {
  heldCallbacks.clear();
}

export function consumeConnectorCallback(): ConnectorCallbackEventDetail | null {
  const detail = parseConnectorCallback(window.location.search);
  if (detail === null) {
    return null;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete("connector");
  url.searchParams.delete("status");
  url.searchParams.delete("reason");
  replaceHistoryEntry(`${url.pathname}?${url.searchParams.toString()}`);

  heldCallbacks.set(detail.connector, detail);
  window.dispatchEvent(
    new CustomEvent<ConnectorCallbackEventDetail>(CONNECTOR_CALLBACK_EVENT, {
      detail,
    }),
  );

  return detail;
}
